import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { gtag } from '../../Analytics/gtag';
import PowerCheckoutForm from '../../PowerCheckoutForm';
import Img from '../../Img';
import Popup from '../../Popup/PopupV2';
import * as popupActions from '../../../actions/popup-actions';
import { PopupTypes, stripePlans, SubscriptionIntervalTypes } from '../../../utils/constants';
import { getPowerSubscriptionPlans, getPowerSubscriptionDetails } from '../../../api/checkoutApi';
import { getApiAxiosInstance } from '../../../utils/network';

import styles from './upgradeV3.scss';
import popupStyles from '../../Popup/popupv2.scss';

const API_STATUS = {
    SUCCESS: 'success',
};

const UpgradeToPowerPopupV3 = (props) => {
    const { YEAR, MONTH } = SubscriptionIntervalTypes;
    const [interval, setInterval] = useState(YEAR);
    const [subscriptionPlans, setSubscriptionPlans] = useState([]);
    const [couponCode, setCouponCode] = useState(null);
    const [couponError, setCouponError] = useState('');
    const [invoiceItems, setInvoiceItems] = useState(null);
    const [isLoadingPricing, setIsLoadingPricing] = useState(false);
    const [isTennisAustraliaMember, setIsTennisAustraliaMember] = useState(false);
    const [binCountryError, setBinCountryError] = useState('');
    const popups = useSelector(({ popups }) => popups);
    const dispatch = useDispatch();

    const fetchSubscriptionPlans = async () => {
        try {
            const response = await getPowerSubscriptionPlans(getApiAxiosInstance());
            if (response?.data?.subscriptionPlans) {
                setSubscriptionPlans(response.data.subscriptionPlans);
            }
            if (response?.data?.isTennisAustraliaMember) {
                setIsTennisAustraliaMember(true);
            }
        } catch (error) {
            // Keep subscriptionPlans as empty array on error
            setSubscriptionPlans([]);
        }
    };

    // Helper function to check if current plan is a v2022 plan
    const isV2022Plan = (targetInterval = null, plansToUse = null) => {
        const intervalToUse = targetInterval || interval;
        const plansArray = plansToUse || subscriptionPlans;
        const selectedPlan = plansArray.find(plan => plan.interval === intervalToUse);
        const planId = selectedPlan?.planId;
        return planId === stripePlans.members.powerAnnual || planId === stripePlans.members.powerMonthly;
    };

    // Only call this function for v2022 plans that need API subscription details
    const fetchSubscriptionDetails = async (targetInterval = null, couponCode = null, plansToUse = null) => {
        try {
            const intervalToUse = targetInterval || interval;
            const plansArray = plansToUse || subscriptionPlans;
            const selectedPlan = plansArray.find(plan => plan.interval === intervalToUse);
            const planId = selectedPlan?.planId;

            if (!planId) {
                if (couponCode) {
                    setCouponError('Plan not available');
                }
                return null;
            }

            const apiParams = {
                plan: planId,
            };

            // Only include couponCode if it has a value
            if (couponCode) {
                apiParams.couponCode = couponCode;
            }

            const response = await getPowerSubscriptionDetails(getApiAxiosInstance(), apiParams);
            if (response.data.status === API_STATUS.SUCCESS) {
                if (couponCode) {
                    setCouponError('');
                }
                return response.data.data;
            }

            if (couponCode) {
                setCouponError(response.data.message || 'Invalid coupon code');
                // Don't clear invoice items on coupon error - keep the base pricing
            }
            return null;
        } catch (error) {
            if (couponCode) {
                setCouponError('Error applying coupon');
                // Don't clear invoice items on coupon error - keep the base pricing
            }
            return null;
        }
    };

    useEffect(() => {
        fetchSubscriptionPlans();
    }, []);

    // Fetch pricing when plans are loaded (initial load only)
    useEffect(() => {
        const loadSubscriptionDetails = async () => {
            if (subscriptionPlans.length > 0) {
                const requiresSubscriptionDetailsForV2022Plan = isV2022Plan();
                if (requiresSubscriptionDetailsForV2022Plan) {
                    const invoiceData = await fetchSubscriptionDetails();
                    setInvoiceItems(invoiceData);
                } else {
                    // Set invoiceItems to null because we are using plan data instead of API response
                    // if it is not a 2022 plan, the plan data will be used instead of the API response
                    // TODO: This is a temporary solution to avoid the API call for non-v2022 plans
                    // SOLUTION: API needs to support all plans
                    setInvoiceItems(null);
                }
            }
        };

        loadSubscriptionDetails();
    }, [subscriptionPlans]);

    const handleSuccess = () => {
        gtag('power-upgrade', {
            category: 'Power Upgrade',
            context: `Upgrade success`,
        });
        dispatch(popupActions.closePopup(PopupTypes.POWER_UPGRADE_POPUP));
        dispatch(popupActions.showPopupNew({
            popup: PopupTypes.POWER_SUCCESS_POPUP_V2,
            redirectTo: popups.redirectTo,
        }));
    };

    const handleBinCountryMismatch = async (availablePowerPlans, errorMessage) => {
        // Set loading to hide pricing during update
        setIsLoadingPricing(true);

        // Update subscription plans
        if (availablePowerPlans.subscriptionPlans && availablePowerPlans.subscriptionPlans.length > 0) {
            setSubscriptionPlans(availablePowerPlans.subscriptionPlans);
        }

        // Update Tennis Australia member status
        if (availablePowerPlans.isTennisAustraliaMember !== undefined) {
            setIsTennisAustraliaMember(availablePowerPlans.isTennisAustraliaMember);
        }

        // Set error message
        setBinCountryError(errorMessage);

        // Reset other states
        setCouponCode(null);
        setCouponError('');

        // Check if we need to fetch new pricing for v2022 plans
        const newPlans = availablePowerPlans.subscriptionPlans || subscriptionPlans;
        const requiresSubscriptionDetailsForV2022Plan = isV2022Plan(interval, newPlans);

        if (requiresSubscriptionDetailsForV2022Plan) {
            const invoiceData = await fetchSubscriptionDetails(interval, null, newPlans);
            setInvoiceItems(invoiceData);
        } else {
            setInvoiceItems(null);
        }

        // Clear loading state - now pricing will show
        setIsLoadingPricing(false);
    };

    const handlePlanClick = async (newInterval) => {
        // Set loading to hide pricing during plan change
        setIsLoadingPricing(true);

        setInterval(newInterval);
        // Reset coupon when changing plans
        setCouponCode(null);
        setCouponError('');

        // Only fetch subscription details for v2022 plans
        if (isV2022Plan(newInterval)) {
            const invoiceData = await fetchSubscriptionDetails(newInterval, null);
            setInvoiceItems(invoiceData); // Will be null on failure, which falls back to planData
        } else {
            // Set invoiceItems to null because we are using plan data instead of API response
            setInvoiceItems(null);
        }

        // Clear loading state - pricing will show with correct data
        setIsLoadingPricing(false);
    };

    const applyCoupon = async (couponCode) => {
        if (!couponCode || couponCode.trim() === '') {
            setCouponError('Please enter a coupon code');
            return;
        }

        const isV2022Plan = isV2022Plan();
        // Only apply coupons for v2022 plans that support API subscription details
        if (isV2022Plan) {
            const invoiceData = await fetchSubscriptionDetails(null, couponCode.trim());
            setInvoiceItems(invoiceData);
            if (invoiceData) {
                setCouponCode(couponCode.trim());
            }
        } else {
            // For non-v2022 plans, coupons are not supported via API
            setCouponError('Coupon codes are not supported for this plan');
        }
    };

    // Helper functions to get plan data
    const getYearlyPlan = () => {
        return subscriptionPlans.find(plan => plan.interval === YEAR);
    };

    const getMonthlyPlan = () => {
        return subscriptionPlans.find(plan => plan.interval === MONTH);
    };

    const getSelectedPlan = () => {
        return subscriptionPlans.find(plan => plan.interval === interval);
    };

    const getSavingsPercentage = () => {
        const yearlyPlan = getYearlyPlan();
        return yearlyPlan ? yearlyPlan.savingsPercentage : 0;
    };

    const modalHeader = () => {
        return (
            <div className={`asdf ${popupStyles.modalHeader} ${styles.header}`}>
                <div className={styles.background}>
                    <Img src="/img/v3/graphics/modals/power-up-your-play-2x.png" />
                </div>
                <div
                    className={`${popupStyles.btnClose} ${styles.close}`}
                    onClick={props.onCloseClick}
                    data-testid="modal.power-upgrade.close-btn"
                >
                    <i className={`utr utr-close`} />
                </div>
            </div>
        );
    };

    return (
        <div className={styles.popup}>
            <Popup
                className="popup-md"
                config={{
                    size: 'md',
                    scrollable: true,
                    customHeader: modalHeader,
                    customWidth: '630px',
                }}
                onCloseClick={props.onCloseClick}
            >
                <div className={styles.content}>
                    <div className={styles.title}>
                        Why subscribe?
                    </div>
                    <div className={styles.subtitle}>
                        A Power subscription enhances your experience both on and off the court! Gain access to exclusive views, perks, deals and more!
                    </div>
                    <div className={styles.perks}>
                        <div className="row">
                            <div className="col-12 col-md-6 mb16">
                                <div className={styles.perk}>
                                    <div className={styles.icon}>
                                        <Img src="/img/v3/icons/two-decimals.svg" />
                                    </div>
                                    <div className={styles.title}>
                                        Full Two-Decimal Rating Display
                                    </div>
                                    <div className={styles.description}>
                                        Unlock two-decimal ratings for everyone on the platform
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 col-md-6 mb16">
                                <div className={styles.perk}>
                                    <div className={styles.icon}>
                                        <Img src="/img/v3/icons/loyalty.svg" />
                                    </div>
                                    <div className={styles.title}>
                                        Discounted Entry Fees
                                    </div>
                                    <div className={styles.description}>
                                        $0 Verified Fees for all events
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 col-md-6 mb16">
                                <div className={styles.perk}>
                                    <div className={styles.icon}>
                                        <Img src="/img/v3/icons/analytics.svg" />
                                    </div>
                                    <div className={styles.title}>
                                        Advanced Analytics
                                    </div>
                                    <div className={styles.description}>
                                        Unleash your performance potential through strategic insights
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 col-md-6 mb16">
                                <div className={styles.perk}>
                                    <div className={styles.icon}>
                                        <Img src="/img/v3/icons/redeem.svg" />
                                    </div>
                                    <div className={styles.title}>
                                        Exclusive Perks
                                    </div>
                                    <div className={styles.description}>
                                        Enjoy discounts on gear and apparel from your favorite brands
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {isTennisAustraliaMember && (
                        <div className="d-flex justify-content-center mb-4">
                            <div className={`${styles.tennisAustraliaSection} col-8`}>
                                <div className={styles.tennisAustraliaHeader}>
                                    <div className={styles.tennisAustraliaLogo}>
                                        <Img src="/img/social/tennis-australia-logo.svg" alt="Tennis Australia" />
                                    </div>
                                    <div className={styles.tennisAustraliaText}>
                                        <div className={styles.tennisAustraliaTitle}>
                                            Tennis Australia members receive the best pricing. Save {getSavingsPercentage()}% on Power.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className={styles.sectionTitle}>
                        Choose your plan:
                    </div>
                    <div className={styles.plan}>
                        <div className="row">
                            <div className="col-12 col-md-6">
                                <div
                                    className={`${styles.button} ${interval === YEAR ? styles.selected : ''}`}
                                    onClick={() => handlePlanClick(YEAR)}
                                    data-testid="modal.power-upgrade.annual-plan-option"
                                >
                                    <div className={`${styles.checkbox} d-flex d-md-none`}>
                                        <Img src="/img/icons/checkmark-icon.svg" />
                                    </div>
                                    <div className={`${styles.label} d-block d-md-none`} data-testid="modal.power-upgrade.annual-plan-name">
                                        Annual Plan
                                    </div>
                                    <div className={`d-none d-md-flex jcsb aic w-100`}>
                                        <div className={`${styles.label}`}>
                                            Annual Plan
                                        </div>
                                        <div className={`${styles.checkbox}`}>
                                            <Img src="/img/icons/checkmark-icon.svg" />
                                        </div>
                                    </div>
                                    <div className={`${styles.info}`}>
                                        <div className={`${styles.price}`}>
                                            <div className={styles.monthly}>
                                                <span className={`${styles.amount} ${binCountryError ? styles.binCountryUpdated : ''}`}>{getYearlyPlan()?.displayMonthlyAmount}</span><span className="fs12 text-normal">/month</span>
                                            </div>
                                            <div className={`${styles.yearly}`}>
                                                <span className={`${styles.amount} ${binCountryError ? styles.binCountryUpdated : ''}`}>{getYearlyPlan()?.displayAmountPayPerRecurringInterval}</span>
                                            </div>
                                        </div>
                                        <div className={`${styles.label}`}>
                                            {getSavingsPercentage() > 0 ? `Save ${getSavingsPercentage()}%` : ''}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 col-md-6">
                                <div
                                    className={`${styles.button} ${interval === MONTH ? styles.selected : ''}`}
                                    onClick={() => handlePlanClick(MONTH)}
                                    data-testid="modal.power-upgrade.monthly-plan-option"
                                >
                                    <div className={`${styles.checkbox} d-flex d-md-none`}>
                                        <Img src="/img/icons/checkmark-icon.svg" />
                                    </div>
                                    <div className={`${styles.label} d-block d-md-none`} data-testid="modal.power-upgrade.monthly-plan-name">
                                        Monthly Plan
                                    </div>
                                    <div className={`d-none d-md-flex jcsb aic w-100`}>
                                        <div className={`${styles.label}`}>
                                            Monthly Plan
                                        </div>
                                        <div className={`${styles.checkbox}`}>
                                            <Img src="/img/icons/checkmark-icon.svg" />
                                        </div>
                                    </div>
                                    <div className={`${styles.info}`}>
                                        <div className={`${styles.price}`}>
                                            <span className={`${styles.amount} ${binCountryError ? styles.binCountryUpdated : ''}`}>{getMonthlyPlan()?.displayAmountPayPerRecurringInterval}</span><span className="fs12 text-normal">/month</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="mt40 mb24">
                        <div className={styles.sectionTitle} data-testid="modal.power-upgrade.plan-title">
                            Choose your plan:
                        </div>
                        <div className={styles.cards}>
                            <div className={styles.cardsTitle} data-testid="modal.power-upgrade.cards-text">
                                We accept all major credit cards.
                            </div>
                            <Img src="/img/v3/graphics/cards/visa.png" />
                            <Img src="/img/v3/graphics/cards/mastercard.png" />
                            <Img src="/img/v3/graphics/cards/amex.png" />
                            <Img src="/img/v3/graphics/cards/discover.png" />
                        </div>
                        {binCountryError && (
                            <div className={styles.binCountryError}>
                                <div className={styles.errorMessage}>
                                    <div className={styles.errorTitle}>
                                        Billing Country Does Not Match Your Residence
                                    </div>
                                    <div className={styles.errorText}>
                                        Our subscription pricing is based on your billing country. The rates have been updated accordingly. Please try again.
                                    </div>
                                </div>
                            </div>
                        )}
                        <PowerCheckoutForm
                            interval={interval}
                            onComplete={handleSuccess}
                            hideHeader
                            themeV3
                            hideSaveCard
                            // this is the fallback for non-v2022 plans to be used for line items
                            planData={getSelectedPlan()}
                            couponCode={couponCode}
                            couponError={couponError}
                            applyCoupon={applyCoupon}
                            // this is the fallback for v2022 plans to be used for line items whiich requires an API call
                            invoiceItems={invoiceItems}
                            onBinCountryMismatch={handleBinCountryMismatch}
                            isLoadingPricing={isLoadingPricing}
                            referringSurface={props.referringSurface}
                            gatedFeature={props.gatedFeature}
                        />
                    </div>
                    <div className={`${styles.disclaimer}`}>
                        By tapping the button, I agree to the terms and automatic charges until I cancel.
                    </div>
                </div>
            </Popup>
        </div>
    );
};

UpgradeToPowerPopupV3.propTypes = {
    onCloseClick: PropTypes.func.isRequired,
    referringSurface: PropTypes.string,
    gatedFeature: PropTypes.string,
};

UpgradeToPowerPopupV3.defaultProps = {
    referringSurface: null,
    gatedFeature: null,
};

export default UpgradeToPowerPopupV3;
